// packages/whiteboard/src/optionsbar/optionsbar-actions.ts
//
// Leaf module owning the shared action types + the FILL/BORDER swatch
// action sets. Lives below `optionsbar-config.ts` so vertical slices
// (rectangle, ellipse, …) can compose their own optionsbar definitions
// without creating an import cycle through `optionsbar-config.ts`.
//
// PRD-F1 §T31 fix: the previous wiring had `optionsbar-config.ts` import
// `RECTANGLE_OPTIONS`/`ELLIPSE_OPTIONS` and the slice configs import
// `FILL_ACTIONS`/`BORDER_ACTIONS` from `optionsbar-config.ts` — strict ESM
// (browser) hit a TDZ ReferenceError because the slice modules evaluated
// before `FILL_ACTIONS` was initialised. Vitest tolerated it; the real
// browser did not.

import type { TextWeight } from '../core/interfaces/board-object.interface.js';

/**
 * Intent eines klickbaren SVG-Felds. Der Optionskreis-Code rechnet pro
 * Objekt-Typ den Intent in die konkrete BoardObject-Property um.
 */
export type ActionIntent =
  | 'fill'
  | 'border-color'
  | 'border-width'
  | 'border-style'
  | 'text-color'
  | 'text-size'
  | 'text-weight'
  | 'text-align'
  | 'list-type'
  | 'text-decoration'
  | 'font-style'
  | 'text-transform'
  | 'text-vertical-align'
  | 'box-shadow'
  | 'background-opacity'
  | 'border-radius'
  | 'v-align'
  | 'z-order'
  | 'board-command'
  | 'align'
  | 'person-icon'
  | 'smiley-icon'
  | 'checkbox-size'
  | 'checkbox-icon'
  | 'line-cap-start'
  | 'line-cap-end'
  | 'line-swap-caps'
  | 'line-path-strategy'
  // Anke, 2026-07-23 — das `_custom`-Feld öffnet den (nativen) Farbwähler und
  // wendet die gewählte Farbe an. `value` unterscheidet, WORAUF: 'fill' oder
  // 'border'. Getrennt von 'fill'/'border-color', deren `value` bereits die
  // konkrete Farbe ist — hier gibt es sie noch nicht, sie kommt erst aus dem
  // Wähler.
  | 'custom-color';

export interface SvgActionEntry {
  intent: ActionIntent;
  value: string | number | TextWeight;
}

export type ActionsMap = Record<string, SvgActionEntry>;

export interface OptionsCircleDef {
  /** Roher SVG-Inhalt (über Vite `?raw` geladen). */
  svg: string;
  /** Schlüssel = SVG-`id` des klickbaren Elements. */
  actions: ActionsMap;
  /**
   * Optional slice-owned mapping from generic `ActionIntent` to the
   * concrete BoardObject property name (PRD-F2 §T14). Returning `null`
   * means the intent does not apply to this slice. If absent, callers
   * fall back to the global `resolveProperty` switch in `optionsbar-config`.
   */
  resolveProperty?: (intent: ActionIntent) => string | null;
}

// Anke, 2026-07-23 — die Slot-Position hier entspricht der Feld-ID im SVG
// (`action-fill-color-<N>` → `FILL_COLORS[N-1]`). part2/part3 haben je EIN Feld
// verloren, dessen ID im SVG nun fehlt. Die Position im Array MUSS trotzdem
// erhalten bleiben, damit alle nachfolgenden IDs weiter auf ihre Farbe zeigen —
// die verwaisten Einträge sind tot (kein SVG-Element, `bindActionHandlers`
// überspringt sie still) und mit DEAD_SWATCH markiert.

/** Platzhalter für ein Slot, dessen SVG-Feld entfernt wurde (nie gezeichnet). */
const DEAD_SWATCH = '#000';

/** 30 Slots für die Füllfarbe (Position = SVG-ID; Slot 2 entfernt). */
const FILL_COLORS: ReadonlyArray<string> = [
  '#666', DEAD_SWATCH /* id2 entfernt */, '#9e976c', '#618da1', '#af1f4a', '#f9b000', '#946379',
  '#00ace9', '#a1b200', '#75947c', '#333', '#b74875', '#b7ae8a', '#ffc33e',
  '#95ad99', '#5bc5f2', '#b58fa1', '#82a5b8', '#b6bc3b', '#fff', // id20: #d6d6d6 → #fff
  '#000',
  '#d5bdca', '#c15f87', '#b3b3b3', '#ffd580', '#c6c19d', '#a5becd',
  '#a1daf8', '#a8baaa', '#cccc60',
];

/** 30 Slots für die Rahmenfarbe (Position = SVG-ID; Slot 17 entfernt). */
const BORDER_COLORS: ReadonlyArray<string> = [
  '#00ace9', '#a1b200', '#75947c', '#946379', '#af1f4a', '#9e976c', '#f9b000',
  '#618da1', '#82a5b8', '#5bc5f2', '#b6bc3b', '#95ad99', '#b58fa1', '#b74875',
  '#b7ae8a', '#ffc33e', DEAD_SWATCH /* id17 entfernt */, '#d8d8d8', '#a5becd', '#a1daf8', '#cccc60', // id18: #d6d6d6 → #d8d8d8
  '#a8baaa', '#d5bdca', '#c15f87', '#c6c19d', '#ffd580', '#666', '#b3b3b3',
  '#333', '#000',
];

export function buildSwatches(
  prefix: string,
  intent: ActionIntent,
  colors: ReadonlyArray<string>,
): ActionsMap {
  const out: ActionsMap = {};
  colors.forEach((c, i) => {
    out[`${prefix}-${i + 1}`] = { intent, value: c };
  });
  return out;
}

/** Re-exported color tables in case downstream tooling wants them. */
export { FILL_COLORS, BORDER_COLORS };

// Slot 0 ist der Transparenz-Knopf (rotes Kreuz im SVG): Füllung verschwindet
// (`''` → der Renderer setzt `fillEnabled: false`), Rand wird durchsichtig
// (`'transparent'` → unsichtbarer Strich). Die Farbfelder liegen wie gehabt auf
// Slot 1…30.
export const FILL_ACTIONS: ActionsMap   = {
  'action-fill-color-0': { intent: 'fill', value: '' },
  'action-fill-custom':  { intent: 'custom-color', value: 'fill' },
  ...buildSwatches('action-fill-color',   'fill',         FILL_COLORS),
};
export const BORDER_ACTIONS: ActionsMap = {
  'action-border-color-0': { intent: 'border-color', value: 'transparent' },
  'action-border-custom':  { intent: 'custom-color', value: 'border' },
  ...buildSwatches('action-border-color', 'border-color', BORDER_COLORS),
};

/**
 * Z-order. Generic (object-type agnostic), so every options circle drawing
 * these two buttons shares this one map.
 *
 * Both jump straight to the extreme — `'front'` = bring-to-front, `'back'` =
 * send-to-back. Single-step (one layer at a time) was dropped for every tool:
 * with several overlapping objects it takes an unpredictable number of clicks
 * to clear a neighbour, and nothing on screen says how many are left. The
 * board's `bringForward()` / `sendBackward()` stay part of the public element
 * API — no button drives them.
 */
export const Z_ORDER_ACTIONS: ActionsMap = {
  'action-forward':  { intent: 'z-order', value: 'front' },
  'action-backward': { intent: 'z-order', value: 'back'  },
};

/**
 * Board commands (clipboard, history, delete) — handled directly by the
 * optionsbar element as commands, independent of the selected object type.
 * Shared by every circle (textbox, sticky, …) that exposes these buttons.
 */
export const BOARD_COMMAND_ACTIONS: ActionsMap = {
  'action-copy':   { intent: 'board-command', value: 'copy' },
  // Anke, 2026-07-21 — eigener Knopf fuer "Duplizieren". Ruft `duplicateSelected()`
  // auf, das es laengst gibt (bislang nur ueber die Aktionsleiste erreichbar):
  // klont die Auswahl um DUPLICATE_OFFSET_PX versetzt, OHNE die Zwischenablage
  // anzufassen. Kopieren bleibt damit reines Kopieren.
  'action-duplicate': { intent: 'board-command', value: 'duplicate' },
  // Anke, 2026-07-21 — Gruppieren und Auflösen. Beide sind nur unter Bedingungen
  // sinnvoll (mindestens zwei eigenständige Teile bzw. mindestens eine Gruppe in
  // der Auswahl); der Optionskreis leitet den Aktiv-Zustand aus
  // `canGroupSelection` / `canUngroupSelection` ab.
  'action-group':   { intent: 'board-command', value: 'group' },
  'action-degroup': { intent: 'board-command', value: 'degroup' },
  'action-cut':    { intent: 'board-command', value: 'cut' },
  'action-paste':  { intent: 'board-command', value: 'paste' },
  'action-undo':   { intent: 'board-command', value: 'undo' },
  'action-redo':   { intent: 'board-command', value: 'redo' },
  'action-delete': { intent: 'board-command', value: 'delete' },
};

/**
 * Anke, 2026-07-16 — "aneinander ausrichten": snap several selected objects to a
 * shared edge of the selection.
 *
 * Wired ONLY into the `default` circle, unlike BOARD_COMMAND_ACTIONS which every
 * circle carries. Aligning needs at least two objects, and a multi-selection is
 * exactly what makes `pickCircleKey` fall back to `default` — so in a per-type
 * circle (= exactly one object selected) these buttons could never do anything
 * anyway. part1 DRAWS them there regardless; an unwired id binds no handler and
 * stays inert, the same way the line circle leaves `action-border-width-0` drawn
 * but unwired. Wiring them into every slice would erode each circle's tested
 * "only my own intents" contract to buy nothing.
 *
 * The coupling to `pickCircleKey` is deliberate and covered by a test in
 * `tests/components/optionsbar-align-wiring.test.ts`.
 */
export const ALIGN_ACTIONS: ActionsMap = {
  'action-align-left':    { intent: 'align', value: 'left' },
  'action-align-hcenter': { intent: 'align', value: 'hcenter' },
  'action-align-right':   { intent: 'align', value: 'right' },
  'action-align-top':     { intent: 'align', value: 'top' },
  'action-align-vcenter': { intent: 'align', value: 'vcenter' },
  'action-align-bottom':  { intent: 'align', value: 'bottom' },
};

/**
 * Border stroke widths for the shared border-width SVG widget. The
 * `border-width` intent is mapped to the concrete property per object type
 * (textbox → `borderWidth`, rectangle/line → `strokeWidth`) by each slice's
 * `resolveProperty`, so this map is generic.
 *
 * Slot 0 is the crossed-out button and means "no stroke" (0). Slots 1..4 are
 * four real widths, thickest to thinnest: 8 / 4 / 2 / 1.
 *
 * part2 DRAWS `action-border-width-0` for shapes, but a line of width 0 would
 * simply vanish — so the line's config drops slot 0 from its wiring, leaving the
 * drawn button inert. Unmatched ids bind no handler, so the same map is safe to
 * share.
 */
export const BORDER_WIDTH_ACTIONS: ActionsMap = {
  'action-border-width-0': { intent: 'border-width', value: 0 },
  'action-border-width-1': { intent: 'border-width', value: 8 },
  'action-border-width-2': { intent: 'border-width', value: 4 },
  'action-border-width-3': { intent: 'border-width', value: 2 },
  'action-border-width-4': { intent: 'border-width', value: 1 },
};

/**
 * Corner radius for the shared border-radius SVG widget — none/8/20. The
 * `border-radius` intent maps per type (textbox → `cornerRadius`, rectangle →
 * `cornerRadius`). The SVG's `action-border-radius-3` slot is not wired.
 */
export const BORDER_RADIUS_ACTIONS: ActionsMap = {
  'action-border-radius-none': { intent: 'border-radius', value: 0 },
  'action-border-radius-1':    { intent: 'border-radius', value: 8 },
  'action-border-radius-2':    { intent: 'border-radius', value: 20 },
};

/** Border style for the shared widget. Slot 1=dashed, 2=dotted, 3=solid. */
export const BORDER_STYLE_ACTIONS: ActionsMap = {
  'action-border-style-1': { intent: 'border-style', value: 'dashed' },
  'action-border-style-2': { intent: 'border-style', value: 'dotted' },
  'action-border-style-3': { intent: 'border-style', value: 'solid'  },
};

/** Drop-shadow toggle (on/off) — the shared box-shadow widget. */
export const BOX_SHADOW_ACTIONS: ActionsMap = {
  'action-box-shadow-1': { intent: 'box-shadow', value: 'on' },
};

/**
 * End caps of a line. `left` is the start point (x1/y1), `right` the end point
 * (x2/y2); slots 1/2/3 are dot / arrow / square, matching the shapes the line
 * circle's SVG draws in each slot.
 *
 * The SVG offers no "no cap" button, so the optionsbar element treats these two
 * intents as toggles that fall back to the `'none'` cap — see `LINE_CAP_INTENTS`.
 * The generic `TOGGLE_INTENTS` path cannot serve: it deletes the property, and
 * `startCap`/`endCap` are required fields the renderer reads unconditionally.
 */
export const LINE_CAP_ACTIONS: ActionsMap = {
  'action-line-end-left-1':  { intent: 'line-cap-start', value: 'dot'    },
  'action-line-end-left-2':  { intent: 'line-cap-start', value: 'arrow'  },
  'action-line-end-left-3':  { intent: 'line-cap-start', value: 'square' },
  'action-line-end-right-1': { intent: 'line-cap-end',   value: 'dot'    },
  'action-line-end-right-2': { intent: 'line-cap-end',   value: 'arrow'  },
  'action-line-end-right-3': { intent: 'line-cap-end',   value: 'square' },
};

/**
 * Swap both ends of every selected line. Not a property write: the swap
 * exchanges the caps, the endpoint coordinates AND the anchors, and all of it
 * has to collapse into a single undo step. The optionsbar element therefore
 * dispatches the `line/swap-caps` UiAction with all selected lines, which
 * `LineLifecycleController` turns into one `SwapLineCapsCommand` per line —
 * pushed as one step (a `CompositeCommand` for several lines).
 */
export const LINE_SWAP_ACTIONS: ActionsMap = {
  'action-change-ends': { intent: 'line-swap-caps', value: 'swap' },
};

/**
 * Line path form. part7 draws three separate buttons — straight, orthogonal
 * ("rectangle"/right-angle elbow) and bezier — each setting its target
 * strategy directly. Like the cap swap this is a command, not a property
 * write: the optionsbar element dispatches `line/set-path` for every selected
 * line that does not have the form yet, and highlights the button whose
 * strategy the line has (several lines: the one all of them share, none if
 * they differ).
 */
export const LINE_PATH_ACTIONS: ActionsMap = {
  // Drei getrennte Knöpfe (part7) — jeder setzt seine Linienführung direkt, der
  // aktive wird hervorgehoben. Der Knopf für rechtwinklig heißt im SVG
  // `…-rectangle`, die Strategie dahinter ist `orthogonal`.
  'action-line-path-straight':  { intent: 'line-path-strategy', value: 'straight'   },
  'action-line-path-rectangle': { intent: 'line-path-strategy', value: 'orthogonal' },
  'action-line-path-bezier':    { intent: 'line-path-strategy', value: 'curved'     },
};

/** Background/fill opacity toggle — slot 1 = 66% opacity. */
export const BACKGROUND_OPACITY_ACTIONS: ActionsMap = {
  'action-background-opacity-1': { intent: 'background-opacity', value: 0.66 },
};

/** 30 colors for the text-color swatches (order matches the SVG). */
const TEXT_COLORS: ReadonlyArray<string> = [
  '#666', '#9e976c', '#af1f4a', '#946379', '#75947c', '#a1b200', '#00ace9',
  '#618da1', '#fff', '#f9b000', '#333', '#d6d6d6', '#82a5b8', '#5bc5f2',
  '#b6bc3b', '#95ad99', '#b58fa1', '#b74875', '#b7ae8a', '#ffc33e',
  '#000',
  '#c6c19d', '#c15f87', '#d5bdca', '#a8baaa', '#cccc60', '#a1daf8',
  '#a5becd', '#b3b3b3', '#ffd580',
];

export { TEXT_COLORS };

/**
 * Text colour swatches. Shared: every circle that draws a `text-color` group
 * maps the intent to its own property (textbox → `color`, person → `textColor`).
 */
export const TEXT_ACTIONS: ActionsMap = buildSwatches('action-text-color', 'text-color', TEXT_COLORS);

/**
 * Font sizes: 8 parent groups × 3 fine steps = 24 sizes (index 1…24). The
 * optionsbar element converts the index to world units via
 * `fontIndexToWorldUnits`. Each group's PARENT button (`action-fontsize-X`) is
 * identical to its FIRST fine step (`action-fontsize-X-1`) — same index, same
 * function (PRD requirement 1/2). Value = the 1-based fine-step index.
 *
 *   action-fontsize-X      → index (X-1)*3 + 1   (== action-fontsize-X-1)
 *   action-fontsize-X-Y    → index (X-1)*3 + Y
 */
function buildFontSizeActions(): ActionsMap {
  const out: ActionsMap = {};
  for (let x = 1; x <= 8; x++) {
    const base = (x - 1) * 3;
    out[`action-fontsize-${x}`] = { intent: 'text-size', value: base + 1 };
    for (let y = 1; y <= 3; y++) {
      out[`action-fontsize-${x}-${y}`] = { intent: 'text-size', value: base + y };
    }
  }
  return out;
}

export const TEXT_SIZE_ACTIONS: ActionsMap = buildFontSizeActions();

/** Font weight. Slot 1=thin (300), 2=normal (500), 3=bold (700). */
export const TEXT_WEIGHT_ACTIONS: ActionsMap = {
  'action-text-weight-1': { intent: 'text-weight', value: 300 },
  'action-text-weight-2': { intent: 'text-weight', value: 500 },
  'action-text-weight-3': { intent: 'text-weight', value: 700 },
};

/** Font style — italic. */
export const FONT_STYLE_ACTIONS: ActionsMap = {
  'action-font-style-italic': { intent: 'font-style', value: 'italic' },
};

/** Text decoration. */
export const TEXT_DECORATION_ACTIONS: ActionsMap = {
  'action-text-decoration-underline':    { intent: 'text-decoration', value: 'underline' },
  'action-text-decoration-line-through': { intent: 'text-decoration', value: 'line-through' },
};
